'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import { getSpeedsizeImageUrl } from 'components/shared/custom-image/CustomImage';
import { FESTIVE_CDN_ASSETS } from '../festiveAssets';
import { useScrollProgress } from './useFestiveScrollMotion';
import { FestiveRisingCard } from './FestiveRisingCard';
import styles from './FestiveV2Hero.module.css';

/**
 * V3 hero (Figma Gifting LP V2): gold copy on the page grey, with a metallic card
 * rising out of its own haze. Line pattern from CDN `hero/hero-mask-lines.svg`.
 */
export const FestiveV2Hero: React.FC = () => {
  const t = useTranslations('performance-lab');
  const sectionRef = useScrollProgress<HTMLElement>();
  const titleLines = t('festive.versions.v2.heroTitle').split('\n');
  /** Always break after “off.”; phones also break before “and”. */
  const body = t.rich('festive.versions.v2.heroBodyLight', {
    mb: () => <br className={styles.brMobile} />,
    ms: (chunks) => <span className={styles.spMobile}>{chunks}</span>,
    tb: () => <br className={styles.brTablet} />,
    ts: (chunks) => <span className={styles.spTablet}>{chunks}</span>,
  });

  return (
    <section
      ref={sectionRef}
      className={styles.section}
      data-tone="light"
      data-rising-card="true"
      data-pl-hero
      aria-label={t('festive.hero.aria')}
    >
      <img
        className={styles.lines}
        src={getSpeedsizeImageUrl(FESTIVE_CDN_ASSETS.heroMaskLines)}
        alt=""
        aria-hidden="true"
        decoding="async"
      />
      {/* Headline inner shadows from Figma 5444:5016. CSS cannot put these on gradient text. */}
      <svg className={styles.filterDefs} aria-hidden="true" focusable="false">
        <filter
          id="festive-title-emboss"
          x="-5%"
          y="-20%"
          width="110%"
          height="150%"
          colorInterpolationFilters="sRGB"
        >
          <feOffset in="SourceAlpha" dy={1.5} result="liftUp" />
          <feComposite
            in="SourceAlpha"
            in2="liftUp"
            operator="out"
            result="lipEdge"
          />
          <feFlood floodColor="#fff" floodOpacity="0.7" />
          <feComposite in2="lipEdge" operator="in" result="lip" />
          <feGaussianBlur in="SourceAlpha" stdDeviation="0.5" result="soft" />
          <feOffset in="soft" dy="-2" result="softUp" />
          <feComposite
            in="SourceAlpha"
            in2="softUp"
            operator="out"
            result="shadeEdge"
          />
          <feFlood floodColor="#5c4220" floodOpacity="0.5" />
          <feComposite in2="shadeEdge" operator="in" result="shade" />
          <feGaussianBlur in="SourceAlpha" stdDeviation="4" result="dropBlur" />
          <feOffset in="dropBlur" dy="3" result="dropOff" />
          <feFlood floodColor="#000a2a" floodOpacity={0} />
          <feComposite in2="dropOff" operator="in" result="drop" />
          <feMerge>
            <feMergeNode in="drop" />
            <feMergeNode in="SourceGraphic" />
            <feMergeNode in="shade" />
            <feMergeNode in="lip" />
          </feMerge>
        </filter>
      </svg>
      {/* Copy before card so mobile in-flow matches Figma 6129:813 (title → body → card). */}
      <div className={styles.copy}>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>
            {titleLines.map((line, i) => (
              <span key={line} className={styles.titleLine}>
                {i > 0 && ' '}
                {line}
              </span>
            ))}
          </h1>
          <p className={styles.body}>{body}</p>
        </div>
      </div>
      <FestiveRisingCard />
    </section>
  );
};
